/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
// TODO: Fix accessibility
// File: components/scenario-planner-details/mass-edit-sidebar.component.tsx

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { hasFilterSelectedValue } from '../../../../components/filters/hooks/common.hook';
import {
  FilterOptionSelectedValue,
  FilterSelectedValue,
} from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import MassEditCustomSelectionModalComponent from '../mass-edit-custom-selection-modal/mass-edit-custom-selection-modal.component';
import MassEditPropertyBlock from '../mass-edit-property-block/mass-edit-property-block.component';
import { SCENARIO_PLANNER_METRICS } from '../scenario-planner.constants';
import './mass-edit-sidebar.component.scss';

interface MassEditSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onRunEdit: (
    massEditBlock: MassEditBlock[],
    applyMode: ApplyTo,
    customSelection?: FilterSelectedValue[],
  ) => Promise<any>;
  defaultEditBlocks?: MassEditBlock[];
  fieldOptions: FilterOptionSelectedValue[];
  defaultApplyMode?: ApplyTo;
  scenarioPlannerFilterSel: FilterSelectedValue[];
  selectedRows: FilterSelectedValue;
}
export type MassEditField = {
  fieldId: string;
  inputType: string;
  disabled?: boolean;
};
export type MassEditBlock = {
  fieldId: string;
  inputType: string;
  value: number | undefined;
};
export type ApplyTo = 'all' | 'filtered' | 'edited' | 'custom';
const MassEditSidebarComponent: FC<MassEditSidebarProps> = ({
  scenarioPlannerFilterSel,
  selectedRows,
  defaultApplyMode = 'filtered',
  isOpen,
  onClose,
  onRunEdit,
  defaultEditBlocks = [],
  fieldOptions,
}) => {
  const [applyTo, setApplyTo] = useState<ApplyTo>(defaultApplyMode);
  const [editBlocks, setEditBlocks] =
    useState<MassEditBlock[]>(defaultEditBlocks);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [isPending, setPending] = useState(false);
  const [customSelection, setCustomSelection] = useState<
    FilterSelectedValue[] | undefined
  >();

  // Check if "Selected" option should be disabled based on selected table rows
  const isSelectedDisabled = useMemo(() => {
    return !hasFilterSelectedValue(selectedRows);
  }, [selectedRows]);

  const handleApplyToChange = (value: ApplyTo) => {
    setApplyTo(value);
    if (value === 'custom') {
      setShowCustomModal(true);
    }
  };
  useEffect(() => {
    // If defaultApplyMode is 'edited' but it should be disabled, fall back to 'filtered'
    if (defaultApplyMode === 'edited' && isSelectedDisabled) {
      setApplyTo('filtered');
    } else {
      handleApplyToChange(defaultApplyMode);
    }
  }, [defaultApplyMode, isSelectedDisabled]);

  // If current selection is 'edited' and it becomes disabled, reset to 'filtered'
  useEffect(() => {
    if (applyTo === 'edited' && isSelectedDisabled) {
      setApplyTo('filtered');
    }
  }, [applyTo, isSelectedDisabled]);

  const updateField = useCallback(
    (index: number, value: string) => {
      const updated = [...editBlocks];
      updated[index].fieldId = value;
      setEditBlocks(updated);
    },
    [editBlocks],
  );

  const updateChangeType = useCallback(
    (index: number, type: string) => {
      const updated = [...editBlocks];
      updated[index].inputType = type;
      setEditBlocks(updated);
    },
    [editBlocks],
  );

  const updateValue = useCallback(
    (index: number, value: string) => {
      const updated = [...editBlocks];
      const val = parseFloat(value);
      updated[index].value = Number.isNaN(val) ? undefined : val;
      setEditBlocks(updated);
    },
    [editBlocks],
  );

  const resetValue = useCallback(
    (index: number) => {
      const updated = [...editBlocks];
      updated[index].value = undefined;
      setEditBlocks(updated);
    },
    [editBlocks],
  );

  // Selected fields must be disabled in next property to prevent from dupplicates.
  const disabledFields = useMemo(() => {
    const fields = editBlocks.map((i) => i.fieldId);
    const dod = editBlocks.find(
      (i) => i.fieldId === SCENARIO_PLANNER_METRICS.DEPTH_OF_DISCOUNT,
    );
    const apup = editBlocks.find(
      (i) => i.fieldId === SCENARIO_PLANNER_METRICS.ANY_PROMO_UNIT_PRICE,
    );
    if (dod) {
      fields.push(SCENARIO_PLANNER_METRICS.ANY_PROMO_UNIT_PRICE);
    } else {
      if (apup) {
        fields.push(SCENARIO_PLANNER_METRICS.DEPTH_OF_DISCOUNT);
      }
    }
    return fields;
  }, [editBlocks]);
  // Disable Add Property button when all fields are selected.
  const addEditPropEnabled = useMemo(() => {
    return editBlocks.length === fieldOptions?.length;
  }, [editBlocks.length, fieldOptions.length]);

  const addProperty = useCallback(() => {
    // Selected fields
    const fields = editBlocks.map((i) => i.fieldId);
    // Looks for next available field option.
    const nextOption = fieldOptions
      .filter((i) => disabledFields.indexOf(i.value) === -1)
      .find(({ value }) => fields.indexOf(value) === -1);
    if (nextOption) {
      setEditBlocks([
        ...editBlocks,
        { fieldId: nextOption.value, inputType: 'perc', value: undefined },
      ]);
    }
  }, [disabledFields, editBlocks, fieldOptions]);

  const handleResetAll = () => {
    setApplyTo('filtered');
    setEditBlocks(defaultEditBlocks);
    setShowCustomModal(false);
  };
  const handleRunEdit = useCallback(async () => {
    setPending(true);
    await onRunEdit(editBlocks, applyTo, customSelection);
    setPending(false);
  }, [applyTo, customSelection, editBlocks, onRunEdit]);
  const handleConfirm = useCallback(
    (filterSelection: FilterSelectedValue[]) => {
      setCustomSelection(filterSelection);
      setShowCustomModal(false);
    },
    [],
  );

  return (
    <>
      <div className={`mass-edit-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="mass-edit-header">
          <h5>Mass Edit</h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        <div className="mass-edit-content">
          <div className="section">
            <label className="section-label">Apply to</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  checked={applyTo === 'all'}
                  onChange={() => handleApplyToChange('all')}
                />
                All
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  checked={applyTo === 'filtered'}
                  onChange={() => handleApplyToChange('filtered')}
                />
                Filtered
              </label>
              <label
                className={`radio-option ${isSelectedDisabled ? 'disabled' : ''}`}
              >
                <input
                  type="radio"
                  checked={applyTo === 'edited'}
                  onChange={() => handleApplyToChange('edited')}
                  disabled={isSelectedDisabled}
                />
                Selected
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  checked={applyTo === 'custom'}
                  onChange={() => handleApplyToChange('custom')}
                />
                <span
                  className="custom-select-link"
                  onClick={() => setShowCustomModal(true)}
                >
                  Select Custom
                </span>
              </label>
            </div>
          </div>

          {editBlocks.map((block, index) => (
            <MassEditPropertyBlock
              key={index}
              index={index}
              selectedField={block.fieldId}
              changeType={block.inputType}
              changeValue={block.value ? `${block.value}` : ''}
              onFieldChange={updateField}
              onChangeTypeChange={updateChangeType}
              onValueChange={updateValue}
              onReset={resetValue}
              disabledFields={disabledFields}
            />
          ))}

          <div className="mass-edit-add-block">
            <button
              className="add-property-btn"
              onClick={addProperty}
              disabled={addEditPropEnabled}
            >
              Add Edit Property +
            </button>
          </div>
        </div>

        <div className="mass-edit-footer">
          <Button
            disabled={isPending}
            className="btn-default"
            onClick={handleResetAll}
          >
            Clear Selection
          </Button>
          <Button
            disabled={isPending}
            className="btn-run"
            onClick={handleRunEdit}
          >
            {isPending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {'Running...'}
              </>
            ) : (
              'Run Edit'
            )}
          </Button>
        </div>
      </div>

      {showCustomModal && (
        <MassEditCustomSelectionModalComponent
          show={showCustomModal}
          onClose={() => setShowCustomModal(false)}
          onConfirm={handleConfirm}
          scenarioPlannerFilterSel={scenarioPlannerFilterSel}
          defaultFilterSelection={customSelection}
        />
      )}
    </>
  );
};

export default MassEditSidebarComponent;
